import { makeStyles, tokens } from '@coreai/fluentui-react';
import { operationRowRevealStyle } from './operationRowReveal';

/**
 * Shared styles for the two connector accordion sections in the operation
 * picker — `ConnectorSection` (pre-loaded built-in operations) and
 * `AzureConnectorSection` (lazy-loaded Azure managed-API operations).
 *
 * Both render the same accordion chrome, sticky header, brand-color
 * accent rail, search box, and "see all" affordance, so they share a
 * single makeStyles definition.
 */
export const useConnectorSectionStyles = makeStyles({
  container: {
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground1,
    marginBottom: tokens.spacingVerticalXS,
  },
  containerNoMargin: {
    marginBottom: 0,
  },
  header: {
    backgroundColor: tokens.colorNeutralBackground1,
    paddingTop: tokens.spacingVerticalS,
    paddingBottom: tokens.spacingVerticalS,
    borderTopLeftRadius: tokens.borderRadiusMedium,
    borderTopRightRadius: tokens.borderRadiusMedium,
    borderBottomLeftRadius: tokens.borderRadiusMedium,
    borderBottomRightRadius: tokens.borderRadiusMedium,
    // Sticky so the connector header stays visible while operations scroll past.
    position: 'sticky',
    top: 0,
    zIndex: 2,
    transitionProperty: 'background-color',
    transitionDuration: tokens.durationFast,
    transitionTimingFunction: tokens.curveEasyEase,
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
    ':active': {
      backgroundColor: tokens.colorNeutralBackground1Pressed,
    },
    // Replace Fluent's default outline-based focus (drawn outside the element
    // via ::after, which is clipped by the scroll container) with an inset
    // box-shadow that paints inside the button and can't be clipped.
    '& button': {
      borderRadius: tokens.borderRadiusMedium,
      gridTemplateColumns: 'auto auto minmax(0, 1fr) auto',
    },
    '& button:focus-visible': {
      outline: 'none',
      boxShadow: `inset 0 0 0 2px ${tokens.colorStrokeFocus2}`,
    },
    '& button:focus-visible::after': {
      display: 'none',
    },
    // Forced-colors / Windows High Contrast: box-shadow is ignored, so fall
    // back to an outline using a system color keyword.
    '@media (forced-colors: active)': {
      '& button:focus-visible': {
        outline: '2px solid CanvasText',
        outlineOffset: '-2px',
        boxShadow: 'none',
      },
    },
    '@media (prefers-reduced-motion: reduce)': {
      transitionDuration: '0.01ms',
    },
  },
  headerExpanded: {
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    // Flatten the inner button's bottom corners so the inset focus ring
    // matches the header/panel seam when expanded.
    '& button': {
      borderBottomLeftRadius: 0,
      borderBottomRightRadius: 0,
    },
  },
  // Applied when the section is rendered in a non-collapsible context
  // (e.g. the search drill-in view). Removes the affordances that
  // imply the header is interactive — no hover/pressed background,
  // no pointer cursor — since toggling is disabled.
  headerStatic: {
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1,
    },
    ':active': {
      backgroundColor: tokens.colorNeutralBackground1,
    },
    '& button': {
      cursor: 'default',
    },
  },
  panel: {
    overflow: 'hidden',
    borderBottomLeftRadius: tokens.borderRadiusMedium,
    borderBottomRightRadius: tokens.borderRadiusMedium,
  },
  connectorInfo: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
  },
  connectorName: {
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  connectorDescription: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  headerControls: {
    display: 'inline-flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    flexShrink: 0,
  },
  badge: {
    flexShrink: 0,
  },
  builtInBadge: {
    flexShrink: 0,
    fontSize: tokens.fontSizeBase100,
    fontWeight: tokens.fontWeightSemibold,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.3px',
  },
  operationsList: {
    paddingTop: tokens.spacingVerticalM,
    paddingBottom: tokens.spacingVerticalM,
    paddingLeft: tokens.spacingHorizontalS,
    paddingRight: tokens.spacingHorizontalM,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    listStyle: 'none',
    margin: 0,
    // Left rail in the connector's brand color. Color is set inline by the
    // parent (data-driven). Keeps the user grounded during long scrolls.
    borderLeftWidth: '3px',
    borderLeftStyle: 'solid',
    borderLeftColor: 'transparent',
  },
  // Staggered enter animation. Per-item delay is driven by `--stagger-index`
  // (set inline) so the reduced-motion guard can zero it out — inline
  // animation-delay would otherwise beat the media query and leave items
  // hidden during the delay (`animationFillMode: 'backwards'`).
  operationEnter: {
    animationName: {
      from: { opacity: 0, transform: 'translateY(4px)' },
      to: { opacity: 1, transform: 'translateY(0)' },
    },
    animationDuration: tokens.durationNormal,
    animationDelay: 'calc(var(--stagger-index, 0) * 30ms)',
    animationTimingFunction: tokens.curveDecelerateMax,
    animationFillMode: 'backwards',
    '@media (prefers-reduced-motion: reduce)': {
      animationDuration: '0.01ms',
      animationDelay: '0ms',
    },
  },
  operationItem: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    paddingTop: tokens.spacingVerticalS,
    paddingBottom: tokens.spacingVerticalS,
    paddingLeft: tokens.spacingHorizontalS,
    paddingRight: tokens.spacingHorizontalS,
    borderRadius: tokens.borderRadiusMedium,
    cursor: 'pointer',
    minHeight: '36px',
    border: 'none',
    width: '100%',
    textAlign: 'left' as const,
    font: 'inherit',
    backgroundColor: 'transparent',
    transitionProperty: 'background-color, transform',
    transitionDuration: tokens.durationFast,
    transitionTimingFunction: tokens.curveEasyEase,
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
    ':active': {
      backgroundColor: tokens.colorNeutralBackground1Pressed,
      transform: 'scale(0.97)',
    },
    ':focus-visible': {
      outline: `2px solid ${tokens.colorBrandStroke1}`,
      outlineOffset: '1px',
    },
    // Reveal the per-operation favorite star + info icon on hover / focus-within.
    ...operationRowRevealStyle,
    '@media (prefers-reduced-motion: reduce)': {
      transitionDuration: '0.01ms',
      ':active': {
        transform: 'none',
      },
    },
  },
  operationName: {
    flex: 1,
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  loadingState: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: tokens.spacingVerticalM,
    gap: tokens.spacingHorizontalS,
  },
  emptyState: {
    padding: tokens.spacingVerticalM,
    textAlign: 'center',
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
  seeAllButton: {
    paddingTop: tokens.spacingVerticalS,
    paddingBottom: tokens.spacingVerticalXS,
    paddingLeft: tokens.spacingHorizontalS,
    paddingRight: tokens.spacingHorizontalS,
    cursor: 'pointer',
    border: 'none',
    backgroundColor: 'transparent',
    width: '100%',
    font: 'inherit',
    color: tokens.colorBrandForeground1,
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightRegular,
    textAlign: 'left',
    ':hover': {
      color: tokens.colorBrandForeground2Hover,
      textDecoration: 'underline',
    },
    ':focus-visible': {
      outline: `2px solid ${tokens.colorBrandStroke1}`,
      outlineOffset: '1px',
      borderRadius: tokens.borderRadiusSmall,
    },
  },
  searchContainer: {
    paddingLeft: tokens.spacingHorizontalM,
    paddingRight: tokens.spacingHorizontalM,
    paddingTop: tokens.spacingVerticalS,
    paddingBottom: tokens.spacingVerticalS,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  searchInput: {
    width: '100%',
  },
  operationListItem: {
    listStyle: 'none',
  },
});
